/* OrdinalLegend.tsx generated by @compiled/babel-plugin v0.28.8 */
import "./OrdinalLegend.compiled.css";
import { ax, ix } from "@compiled/react/runtime";
import Tooltip from '@atlaskit/tooltip';
import { Fragment, useRef, useState } from 'react';
import useActiveSeriesKeyEmitter from '../../charts/common/events/hooks/useActiveSeriesKeyEmitter';
import { element, useElementProps } from '../../core/elements';
import { Layout } from '../container/layout';
import { LABEL_LIMIT, LEGEND_MARK_HEIGHT, MAX_LEGEND_ENTRIES, SYMBOL_SIZE } from './constants';
import { useFlatData, useTruncationIndexObserver } from './utils';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
export const legendLabelStyle = {
  font: "var(--ds-font-heading-xxsmall, normal 600 12px/16px ui-sans-serif, -apple-system, BlinkMacSystemFont, \"Segoe UI\", Ubuntu, system-ui, \"Helvetica Neue\", sans-serif)",
  color: "var(--ds-text-subtlest, #626F86)",
  textOverflow: 'ellipsis',
  overflow: 'hidden',
  whiteSpace: 'nowrap'
};
const LegendSymbol = _ref => {
  let {
    color,
    shape
  } = _ref;
  let symbol;
  switch (shape) {
    case 'square':
      symbol = /*#__PURE__*/_jsx("rect", {
        fill: color,
        width: SYMBOL_SIZE,
        height: SYMBOL_SIZE,
        rx: 2,
        ry: 2
      });
      break;
    case 'line':
      symbol = /*#__PURE__*/_jsx("line", {
        x1: 0,
        y1: SYMBOL_SIZE / 2,
        x2: SYMBOL_SIZE,
        y2: SYMBOL_SIZE / 2,
        stroke: color,
        strokeWidth: 2
      });
      break;
    case 'circle':
    default:
      symbol = /*#__PURE__*/_jsx("rect", {
        fill: color,
        width: SYMBOL_SIZE,
        height: SYMBOL_SIZE,
        rx: SYMBOL_SIZE / 2,
        ry: SYMBOL_SIZE / 2
      });
  }
  return /*#__PURE__*/_jsx("div", {
    className: ax(["_1e0c1txw _1bah1h6o _4cvr1h6o"]),
    children: /*#__PURE__*/_jsx("svg", {
      width: SYMBOL_SIZE,
      height: SYMBOL_SIZE,
      children: symbol
    })
  });
};
export const LegendLabel = element(_ref2 => {
  let {
    labelAccessor,
    datum,
    limit
  } = _ref2;
  const ref = useRef(null);
  return /*#__PURE__*/_jsx(Tooltip, {
    content: labelAccessor(datum),
    canAppear: () => {
      const element = ref.current;
      if (!element) return false;
      return element.offsetWidth < element.scrollWidth;
    },
    children: /*#__PURE__*/_jsx("div", {
      ref: ref,
      style: {
        maxWidth: limit
      },
      className: ax(["_11c816g6 _syaz131l _1bto1l2s _1reo15vq _18m915vq _o5721q9c"]),
      children: labelAccessor(datum)
    })
  });
});
const VerticalLegendMark = _ref3 => {
  let {
    color,
    labelAccessor,
    shape,
    opacity,
    datum
  } = _ref3;
  const label = labelAccessor(datum);
  const {
    emitActiveSeriesKey
  } = useActiveSeriesKeyEmitter();
  return /*#__PURE__*/_jsxs("div", {
    style: {
      opacity
    },
    onPointerEnter: e => emitActiveSeriesKey(e, label),
    onPointerLeave: e => emitActiveSeriesKey(e, undefined),
    className: ax(["_1e0c1txw _zulpu2gc _p12f1osq _4t3i7vkz _1ul9idpf _19pk1b66 _otyr1b66"]),
    children: [/*#__PURE__*/_jsx(LegendSymbol, {
      color: color,
      shape: shape
    }), /*#__PURE__*/_jsx(LegendLabel, {
      datum: datum,
      labelAccessor: labelAccessor,
      limit: LABEL_LIMIT
    })]
  });
};
const HorizontalLegendMark = _ref4 => {
  let {
    color,
    labelAccessor,
    shape,
    opacity,
    datum
  } = _ref4;
  const label = labelAccessor(datum);
  const {
    emitActiveSeriesKey
  } = useActiveSeriesKeyEmitter();
  return /*#__PURE__*/_jsxs("div", {
    style: {
      opacity
    },
    onPointerEnter: e => emitActiveSeriesKey(e, label),
    onPointerLeave: e => emitActiveSeriesKey(e, undefined),
    className: ax(["_1e0c116y _zulpu2gc _4t3i7vkz _1ul9idpf"]),
    children: [/*#__PURE__*/_jsx(LegendSymbol, {
      color: color,
      shape: shape
    }), /*#__PURE__*/_jsx(LegendLabel, {
      labelAccessor: labelAccessor,
      datum: datum,
      limit: LABEL_LIMIT
    })]
  });
};
function VerticalLegend(_ref5) {
  let {
    data,
    colorAccessor,
    colorScale,
    labelAccessor,
    shape,
    justifyContent = 'center',
    maxRows = 10
  } = _ref5;
  const legendRef = useRef(null);
  const [truncationIndex, setTruncationIndex] = useState(MAX_LEGEND_ENTRIES);
  const flatData = useFlatData({
    labelAccessor,
    data
  });
  useTruncationIndexObserver({
    legendRefCurrent: legendRef.current,
    setTruncationIndex
  });
  const itemsToTruncate = flatData.length - (truncationIndex || 0);
  return /*#__PURE__*/_jsx("div", {
    ref: legendRef,
    style: {
      maxHeight: `${maxRows * LEGEND_MARK_HEIGHT + 2}px`,
      justifyContent
    },
    className: ax(["_1e0c116y _1bah1h6o _1n261g80 _16jlidpf _1o9zidpf _i0dl1r5k _2lx21bp4 _ae4v1y6m _19pk1wug _2hwx1wug _otyr1wug _18u01wug _19bv1crf _4t3i1osq _1reo15vq _18m915vq _gy1ppxbi"]),
    children: flatData.map((d, i) => /*#__PURE__*/_jsx(Fragment, {
      children: itemsToTruncate > 0 && truncationIndex - 1 <= i ? /*#__PURE__*/_jsx(_Fragment, {
        children: /*#__PURE__*/_jsxs("div", {
          style: {
            position: 'relative'
          },
          children: [/*#__PURE__*/_jsx(VerticalLegendMark, {
            color: colorScale(colorAccessor(d)),
            labelAccessor: labelAccessor,
            shape: shape,
            opacity: 0,
            datum: d
          }, labelAccessor(d)), truncationIndex - 1 === i && /*#__PURE__*/_jsx("div", {
            style: {
              position: 'absolute',
              top: 0,
              left: 0
            },
            className: ax(["_11c816g6 _syaz131l _1bto1l2s _1reo15vq _18m915vq _o5721q9c"]),
            children: ` ... ${itemsToTruncate + 1} more`
          })]
        })
      }) : /*#__PURE__*/_jsx(VerticalLegendMark, {
        color: colorScale(colorAccessor(d)),
        labelAccessor: labelAccessor,
        shape: shape,
        datum: d
      }, labelAccessor(d))
    }, `vertical-leged-entry${labelAccessor(d)}-${colorScale(colorAccessor(d))}-${i}`))
  });
}
function HorizontalLegend(_ref6) {
  let {
    data,
    colorAccessor,
    colorScale,
    labelAccessor,
    shape,
    justifyContent = 'flex-start',
    flexDirection = 'row',
    maxRows = 5
  } = _ref6;
  const legendRef = useRef(null);
  const [truncationIndex, setTruncationIndex] = useState(MAX_LEGEND_ENTRIES);
  const flatData = useFlatData({
    labelAccessor,
    data
  });
  useTruncationIndexObserver({
    legendRefCurrent: legendRef.current,
    setTruncationIndex
  });
  const itemsToTruncate = flatData.length - (truncationIndex || 0);
  return /*#__PURE__*/_jsx("div", {
    ref: legendRef,
    className: ax(["_1e0c116y _1n261g80 _16jlidpf _1o9zidpf _i0dl1wug _2lx21o5q _ae4v1y6m _1bsb1osq _1reo15vq _18m915vq _gy1ppxbi _1p57u2gc _otyrpxbi"]),
    style: {
      // Max rows * legened height +
      maxHeight: `${maxRows * LEGEND_MARK_HEIGHT + 2 + (maxRows - 1) * 8}px`,
      justifyContent,
      "--_j9pi5t": ix(flexDirection)
    },
    children: flatData.map((d, i) => /*#__PURE__*/_jsx(Fragment, {
      children: itemsToTruncate > 0 && truncationIndex - 1 === i ? /*#__PURE__*/_jsx(_Fragment, {
        children: /*#__PURE__*/_jsxs("div", {
          style: {
            position: 'relative'
          },
          children: [/*#__PURE__*/_jsx(HorizontalLegendMark, {
            color: colorScale(colorAccessor(d)),
            labelAccessor: labelAccessor,
            shape: shape,
            opacity: 0,
            datum: d
          }, labelAccessor(d)), /*#__PURE__*/_jsx("div", {
            style: {
              position: 'absolute',
              top: 0,
              left: 0
            },
            className: ax(["_11c816g6 _syaz131l _1bto1l2s _1reo15vq _18m915vq _o5721q9c"]),
            children: ` ... ${itemsToTruncate + 1} more`
          })]
        })
      }) : /*#__PURE__*/_jsx(HorizontalLegendMark, {
        color: colorScale(colorAccessor(d)),
        labelAccessor: labelAccessor,
        shape: shape,
        datum: d
      }, labelAccessor(d))
    }, `horizontal-leged-entry${labelAccessor(d)}-${colorScale(colorAccessor(d))}-${i}`))
  });
}
export function OrdinalLegendPure(_ref7) {
  var _layoutProps$gridTemp;
  let {
    data,
    colorAccessor,
    colorScale,
    labelAccessor,
    shape,
    maxRows,
    justifyContent,
    flexDirection = 'row',
    hide = false
  } = _ref7;
  const layoutProps = useElementProps(Layout);
  const sideBySide = !((_layoutProps$gridTemp = layoutProps.gridTemplate) !== null && _layoutProps$gridTemp !== void 0 && _layoutProps$gridTemp.includes('legend'));
  if (hide) return null;
  if (sideBySide) {
    return /*#__PURE__*/_jsx(VerticalLegend, {
      data: data,
      colorAccessor: colorAccessor,
      colorScale: colorScale,
      labelAccessor: labelAccessor,
      shape: shape,
      maxRows: maxRows,
      justifyContent: justifyContent
    });
  }
  return /*#__PURE__*/_jsx(HorizontalLegend, {
    data: data,
    colorAccessor: colorAccessor,
    colorScale: colorScale,
    labelAccessor: labelAccessor,
    shape: shape,
    maxRows: maxRows,
    justifyContent: justifyContent,
    flexDirection: flexDirection
  });
}
const _OrdinalLegend = element(OrdinalLegendPure);
export { _OrdinalLegend as OrdinalLegend };